import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { api, errMsg } from "./api";
import { Shell, Panel, BigBtn, GhostBtn, Spinner, inputCls } from "./ui";

const TABS = ["My classes", "Cover available", "History"];

export default function Scheduler() {
  const [tab, setTab] = useState("My classes");
  const [my, setMy] = useState(null);
  const [offers, setOffers] = useState(null);
  const [history, setHistory] = useState(null);
  const [covering, setCovering] = useState(null);
  const [mode, setMode] = useState("all");
  const [reason, setReason] = useState("");
  const [colleagues, setColleagues] = useState([]);
  const [prefIds, setPrefIds] = useState([]);

  const load = () => {
    api.get("/sched/my").then((r) => setMy(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/sched/cover-available").then((r) => setOffers(r.data.offers)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "History") api.get("/sched/history").then((r) => setHistory(r.data.events)).catch(() => {});
  }, [tab]);

  const confirm = async (c) => {
    try { await api.post(`/sched/occurrences/${c.occ_id}/confirm`); toast.success("Class confirmed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const requestCover = async (c) => {
    try {
      const { data } = await api.post(`/sched/occurrences/${c.occ_id}/request-cover`,
        { mode, reason, preferred_ids: mode === "preferred" ? prefIds : [] });
      toast.success(data.message);
      setCovering(null); setReason(""); setPrefIds([]);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const act = async (offerId, action) => {
    try {
      const { data } = await api.post(`/sched/offers/${offerId}`, { action });
      toast.success(data.message || `${action} recorded`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const hoursLeft = (c) => {
    const d = new Date(`${c.date}T${c.start_local}:00+08:00`);
    return (d - Date.now()) / 3600000;
  };

  const STATUS_C = { assigned: "text-white/60", covered: "text-emerald-400", uncovered: "text-[#ff2e4d]", cover_requested: "text-amber-400" };
  const STATUS_T = {
    assigned: "On your roster", covered: "Covered class — you're teaching it",
    uncovered: "Cover being arranged — class still running", cover_requested: "Cover being arranged — class still running",
    cancelled: "Cancelled", completed: "Completed",
  };

  return (
    <Shell title="Trainer scheduler" back="/staff">
      <div className="flex gap-1.5 overflow-x-auto pb-3" data-testid="scheduler-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`sched-tab-${t.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setTab(t)}
            className={`min-h-[40px] px-4 rounded-full border text-sm whitespace-nowrap ${tab === t ? "border-[#ff2e4d] text-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 text-white/55"}`}>{t}</button>
        ))}
      </div>

      {tab === "My classes" && (!my ? <Spinner /> : (
        <div className="space-y-3" data-testid="my-classes">
          {my.classes.length === 0 && (
            <Panel className="p-5 text-center text-white/45 text-sm" data-testid="no-classes">
              No upcoming classes on your roster yet. Once management activates the roster, your classes appear here.
            </Panel>
          )}
          {my.classes.map((c) => (
            <Panel key={c.occ_id} className="p-4" data-testid={`class-${c.occ_id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="font-semibold">{c.day} {c.date} · {c.start_12h}</p>
                  <p className={`text-xs ${STATUS_C[c.status] || "text-white/50"}`}>
                    {STATUS_T[c.status] || c.status}
                    {c.confirmation === "confirmed" ? " · confirmed ✓" : c.confirmation === "awaiting" && c.status === "assigned" ? " · awaiting your confirmation" : ""}
                    {c.provisional ? " · Sunday — rotation not configured" : ""}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {c.confirmation === "awaiting" && c.status === "assigned" && (
                    <GhostBtn data-testid={`confirm-${c.occ_id}`} onClick={() => confirm(c)} className="text-emerald-400 border-emerald-400/40 text-xs">Confirm</GhostBtn>
                  )}
                  {c.status === "assigned" && (
                    <GhostBtn data-testid={`cover-${c.occ_id}`} onClick={() => { setCovering(c); setMode("all"); setPrefIds([]); api.get("/sched/colleagues").then((r) => setColleagues(r.data.colleagues.filter((x) => !x.draft))); }} className="text-xs">Request cover</GhostBtn>
                  )}
                  {c.status === "covered" && (
                    <span className="text-[10px] text-white/35 self-center">Can't make it? Call Dan 0415 853 322</span>
                  )}
                </div>
              </div>
              {covering?.occ_id === c.occ_id && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-3" data-testid="cover-form">
                  {hoursLeft(c) < 24 && (
                    <div className="rounded-xl border border-[#ff2e4d]/50 bg-[#ff2e4d]/10 p-3 text-sm" data-testid="late-warning">
                      <p>This class starts in less than 24 hours, so arranging cover may be difficult. Please call Dan on 0415 853 322 and, where possible, contact another trainer directly to help arrange cover. Your request will also alert management and begin the cover search.</p>
                      <a href="tel:+61415853322" data-testid="call-dan-button"
                        className="inline-flex items-center gap-2 mt-2 min-h-[44px] px-4 rounded-xl bg-[#ff2e4d] text-black font-bold text-xs uppercase"><Phone size={14} /> Call Dan</a>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {[["all", "Ask all eligible trainers now"], ["preferred", "Ask preferred trainers first"]].map(([k, l]) => (
                      <button key={k} data-testid={`cover-mode-${k}`} onClick={() => setMode(k)}
                        className={`min-h-[48px] rounded-xl border text-xs p-2 ${mode === k ? "border-[#ff2e4d] text-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 text-white/55"}`}>{l}</button>
                    ))}
                  </div>
                  {mode === "preferred" && (
                    <div className="space-y-2">
                      <p className="text-xs text-white/45">Preferred trainers get first opportunity; if no one accepts by the deadline the request expands to all eligible trainers.</p>
                      <div className="flex flex-wrap gap-2" data-testid="preferred-picker">
                        {colleagues.map((co) => (
                          <button key={co.trainer_id} data-testid={`pref-${co.name}`}
                            onClick={() => setPrefIds((ids) => ids.includes(co.trainer_id) ? ids.filter((x) => x !== co.trainer_id) : [...ids, co.trainer_id])}
                            className={`min-h-[40px] px-3 rounded-full border text-xs ${prefIds.includes(co.trainer_id) ? "border-[#ff2e4d] text-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 text-white/55"}`}>
                            {co.name}
                          </button>
                        ))}
                        {colleagues.length === 0 && <p className="text-xs text-white/35">No other active trainers yet</p>}
                      </div>
                    </div>
                  )}
                  <input data-testid="cover-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional, internal)" className={inputCls} />
                  <p className="text-xs text-amber-400">Requesting cover does not mean the class has been cancelled.</p>
                  <div className="flex gap-2">
                    <BigBtn data-testid="cover-submit-button" onClick={() => requestCover(c)} className="flex-1 text-sm">Continue with cover request</BigBtn>
                    <GhostBtn onClick={() => setCovering(null)}>Back</GhostBtn>
                  </div>
                </div>
              )}
            </Panel>
          ))}
        </div>
      ))}

      {tab === "Cover available" && (!offers ? <Spinner /> : (
        <div className="space-y-3" data-testid="cover-available">
          {offers.length === 0 && <p className="text-white/45 text-sm text-center py-6">No open cover requests right now.</p>}
          {offers.map((o) => (
            <Panel key={o.offer_id} className="p-4" data-testid={`offer-${o.offer_id}`}>
              <p className="font-semibold">{o.day} {o.date} · {o.start_12h}</p>
              <p className="text-xs text-white/45">Originally {o.original_trainer} · {o.status}</p>
              <div className="flex gap-2 mt-3">
                <BigBtn data-testid={`accept-${o.offer_id}`} onClick={() => act(o.offer_id, "accept")} className="flex-1 text-sm">Accept — I'll cover it</BigBtn>
                <GhostBtn data-testid={`decline-${o.offer_id}`} onClick={() => act(o.offer_id, "decline")}>Decline</GhostBtn>
                <GhostBtn data-testid={`mute-${o.offer_id}`} onClick={() => act(o.offer_id, "mute")} className="text-xs">Mute this class</GhostBtn>
              </div>
            </Panel>
          ))}
        </div>
      ))}

      {tab === "History" && (!history ? <Spinner /> : (
        <div className="space-y-1.5" data-testid="sched-history">
          {history.length === 0 && <p className="text-white/45 text-sm text-center py-6">No scheduler activity yet.</p>}
          {history.map((ev) => (
            <p key={ev.event_id} className="text-xs text-white/55 border-l-2 border-white/10 pl-3 py-0.5">
              {ev.action} {ev.entity} <span className="block text-white/25">{new Date(ev.at).toLocaleString()}</span>
            </p>
          ))}
        </div>
      ))}
    </Shell>
  );
}
